import React, { useState, useEffect } from "react";
import { Table, Button, Badge, Modal, Form, Card, Row, Col, Spinner, Pagination } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import * as XLSX from "xlsx";

const LeaveAdminManagement = () => {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [currentRequest, setCurrentRequest] = useState({
    leaveType: 0, startDate: "", endDate: "", totalDays: 0,
    reason: "", attachmentPath: "", requesterName: ""
  });

  const [filters, setFilters] = useState({
    fromDate: "", toDate: "", userId: "", approverId: ""
  });

  const leaveTypeMap = {
    0: "Annual Leave", 1: "Sick Leave", 2: "Unpaid Leave",
    3: "Maternity Leave", 4: "Wedding Leave", 5: "Annual Leave (>= 2 days)"
  };

  const statusMap = {
    [-1]: "MANAGER REJECTED", [0]: "PENDING MANAGER", [1]: "MANAGER APPROVED",
    [2]: "HR REJECTED", [3]: "PENDING HR", [4]: "HR APPROVED",
    [5]: "BLOCK REJECTED", [6]: "PENDING BLOCK MANAGER", [7]: "BLOCK APPROVED",
    [8]: "BOARD REJECTED", [9]: "PENDING BOARD", [10]: "BOARD APPROVED", [11]: "FULLY APPROVED"
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axiosClient.get(API_ENDPOINTS.users);
        setEmployees(res.data);
      } catch (err) { console.error("Load employees error:", err); }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchAdminRequests();
  }, [filters]);

  const fetchAdminRequests = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(API_ENDPOINTS.adminManagementList, { params: filters });
      setRequests(res.data);
      setCurrentPage(1);
    } catch (err) { console.error("Load admin requests error:", err); }
    finally { setLoading(false); }
  };

  const toggleLogs = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = [...requests].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key] || "";
    let bValue = b[sortConfig.key] || "";
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Phân trang
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const exportToExcel = () => {
    const dataToExport = sortedData.map(r => ({
      "Requester": r.requesterName,
      "Leave Type": leaveTypeMap[r.type] || "Other",
      "Start Date": r.startDate ? r.startDate.split('T')[0] : "",
      "End Date": r.endDate ? r.endDate.split('T')[0] : "",
      "Days": r.totalDays,
      "Status": statusMap[r.status] || "UNKNOWN",
      "Current Approver": r.currentApproverName || "N/A"
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LeaveRequests");
    XLSX.writeFile(workbook, "Leave_Management_Report.xlsx");
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleView = (req) => {
    setCurrentRequest({
      leaveType: req.type,
      startDate: req.startDate.split('T')[0],
      endDate: req.endDate.split('T')[0],
      totalDays: req.totalDays,
      reason: req.reason,
      attachmentPath: req.attachmentPath,
      requesterName: req.requesterName
    });
    setShowModal(true);
  };

  const renderStatus = (status) => {
    const badgeProps = {
      [-1]: { bg: "danger", text: "white" }, [0]: { bg: "warning", text: "dark" },
      [1]: { bg: "success", text: "white" }, [2]: { bg: "danger", text: "white" },
      [3]: { bg: "info", text: "white" }, [4]: { bg: "success", text: "white" },
      [5]: { bg: "danger", text: "white" }, [6]: { bg: "warning", text: "dark" },
      [7]: { bg: "success", text: "white" }, [8]: { bg: "danger", text: "white" },
      [9]: { bg: "info", text: "white" }, [10]: { bg: "success", text: "white" },
      [11]: { bg: "success", text: "white" },
    };
    const p = badgeProps[status] || { bg: "secondary", text: "white" };
    return <Badge bg={p.bg} text={p.text} className="fw-normal rounded-pill px-3">{statusMap[status] || "UNKNOWN"}</Badge>;
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-dark mb-0 text-uppercase">Admin Leave Management</h3>
        <Button variant="success" className="rounded-pill" onClick={exportToExcel}>
          <i className="fas fa-file-excel me-2"></i>Export Excel
        </Button>
      </div>

      <Card className="shadow-sm border-0 rounded-3 mb-4">
        <Card.Body className="p-3">
          <Row className="g-3">
            <Col md={3}><Form.Label className="small fw-bold text-muted">FROM DATE</Form.Label><Form.Control type="date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} size="sm" /></Col>
            <Col md={3}><Form.Label className="small fw-bold text-muted">TO DATE</Form.Label><Form.Control type="date" name="toDate" value={filters.toDate} onChange={handleFilterChange} size="sm" /></Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">REQUESTER</Form.Label><Form.Select name="userId" value={filters.userId} onChange={handleFilterChange} size="sm"><option value="">All Requesters</option>{employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}</Form.Select></Col>
            <Col md={2}><Form.Label className="small fw-bold text-muted">APPROVER</Form.Label><Form.Select name="approverId" value={filters.approverId} onChange={handleFilterChange} size="sm"><option value="">All Approvers</option>{employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}</Form.Select></Col>
            <Col md={2} className="d-flex align-items-end"><Button variant="secondary" size="sm" className="w-100 rounded-pill" onClick={() => setFilters({fromDate:"", toDate:"", userId:"", approverId:""})}>RESET</Button></Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                {[ {k:'requesterName', l:'Requester'}, {k:'type', l:'Leave Type'}, {k:'startDate', l:'Duration'}, {k:'totalDays', l:'Days'}, {k:'status', l:'Status'}, {k:'currentApproverName', l:'Current Approver'}, {k:'actions', l:'Actions'} ].map(h => (
                  <th key={h.k} className="ps-4 py-3 text-muted fw-bold small text-uppercase" onClick={() => handleSort(h.k)} style={{cursor: 'pointer'}}>
                    {h.l} {sortConfig.key === h.k ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr> :
                currentItems.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-bottom">
                      <td className="ps-4 py-3"><div className="fw-bold text-primary">{r.requesterName}</div></td>
                      <td><div className="fw-bold text-dark">{leaveTypeMap[r.type] || "Other"}</div></td>
                      <td><div className="small fw-bold text-secondary">{new Date(r.startDate).toLocaleDateString("en-GB")} → {new Date(r.endDate).toLocaleDateString("en-GB")}</div></td>
                      <td className="text-center"><span className="badge rounded-pill bg-light text-dark border px-3">{r.totalDays}</span></td>
                      <td>{renderStatus(r.status)}</td>
                      <td><span className="small fw-bold text-dark">{r.currentApproverName || "N/A"}</span></td>
                      <td>
                        <Button variant="link" size="sm" className="text-decoration-none" onClick={() => toggleLogs(r.id)}>
                            {expandedRows[r.id] ? "[-] Hide Logs" : "[+] View Logs"}
                        </Button>
                        <Button variant="white" className="rounded-circle shadow-sm border text-info ms-2" style={{ width: "35px", height: "35px" }} onClick={() => handleView(r)}><i className="fas fa-eye"></i></Button>
                      </td>
                    </tr>
                    {expandedRows[r.id] && (
                        <tr className="bg-light">
                            <td colSpan="7" className="p-3">
                                <Table size="sm" className="mb-0">
                                    <thead><tr><th>Step</th><th>Approver</th><th>Status</th><th>Comment</th><th>Processed At</th></tr></thead>
                                    <tbody>
                                        {r.approvalLogs?.map((log, index) => (
                                            <tr key={index}>
                                                <td>{log.stepName}</td>
                                                <td>{log.approverName}</td>
                                                <td>{log.action}</td>
                                                <td>{log.note}</td>
                                                <td>{log.actionDate}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </td>
                        </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-center mt-4">
        <Pagination>
          <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} />
        </Pagination>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white border-0"><Modal.Title className="fw-bold fs-5 text-uppercase">Request Details</Modal.Title></Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Row className="mb-4">
              <Col md={12} className="mb-3"><div className="p-3 bg-light rounded border"><span className="small text-muted d-block text-uppercase fw-bold">Requester</span><span className="fs-5 fw-bold text-primary">{currentRequest.requesterName}</span></div></Col>
              <Col md={6}><Form.Label className="small fw-bold text-muted">START DATE</Form.Label><Form.Control type="text" value={currentRequest.startDate} readOnly /></Col>
              <Col md={6}><Form.Label className="small fw-bold text-muted">END DATE</Form.Label><Form.Control type="text" value={currentRequest.endDate} readOnly /></Col>
            </Row>
            <Row className="mb-4">
              <Col md={6}><Form.Label className="small fw-bold text-muted">CATEGORY</Form.Label><Form.Control type="text" value={leaveTypeMap[currentRequest.leaveType] || "Other"} readOnly /></Col>
              <Col md={6}><Form.Label className="small fw-bold text-muted">TOTAL DAYS</Form.Label><Form.Control type="text" value={currentRequest.totalDays} readOnly className="bg-light fw-bold text-primary" /></Col>
            </Row>
            <Form.Group className="mb-4"><Form.Label className="small fw-bold text-muted">ATTACHMENT</Form.Label><div className="mt-1">{currentRequest.attachmentPath ? (<a href={currentRequest.attachmentPath} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm rounded-pill px-3"><i className="fas fa-paperclip me-2"></i>View Attachment</a>) : (<span className="text-muted italic small">No attachment provided</span>)}</div></Form.Group>
            <Form.Group><Form.Label className="small fw-bold text-muted">REASON</Form.Label><Form.Control as="textarea" rows={3} value={currentRequest.reason} readOnly /></Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 p-4"><Button variant="secondary" className="px-4 fw-bold rounded-pill" onClick={() => setShowModal(false)}>CLOSE</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeaveAdminManagement;